import { useMutation, useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';

import { GlossaryListQueryResult } from '../api/types.js';
import { DISABLED_CHANNELS } from '../constants/channelsGlossary.js';
import {
  getPaginatedDataPlaceholder,
  INFINITYISH,
} from '../constants/tablesConfig.js';
import { useImportHubV2 } from '../hooks/ff/index.js';
import { useEnhancedMutation, useTypedQuery } from '../hooks/index.js';
import { mdmFieldCodec } from '../utils/index.js';
import { initialSearchParams } from './queryClient.js';
import { useChannelsCatalog, usePlatformsCatalog } from './resources.js';

const GLOSSARY_KEY_SEGMENT = 'glossaries';

export const useGlossaryListV2 = () => {
  const searchParams = {
    ...initialSearchParams,
    itemsPerPage: INFINITYISH,
  };

  const channelsGlossaryListQuery = useTypedQuery({
    ...DataIngestion.Glossaries.getGlossaryListV2QueryOptions({
      params: {
        glossaryType: 'urn:mint:field:channel_v2',
        ...searchParams,
      },
    }),
    placeholderData: getPaginatedDataPlaceholder({
      codesPerGlossary: 0,
    }) as GlossaryListQueryResult,
    staleTime: 30 * 1000,
  });

  const platformsGlossaryListQuery = useTypedQuery({
    ...DataIngestion.Glossaries.getGlossaryListV2QueryOptions({
      params: {
        glossaryType: 'urn:mint:field:platform_v2',
        ...searchParams,
      },
    }),
    placeholderData: getPaginatedDataPlaceholder({
      codesPerGlossary: 0,
    }) as GlossaryListQueryResult,
    staleTime: 30 * 1000,
  });

  const platformsCatalogQuery = usePlatformsCatalog();
  const channelsCatalogQuery = useChannelsCatalog();

  const channelsGlossaryList = channelsGlossaryListQuery.data;
  const platformsGlossaryList = platformsGlossaryListQuery.data;
  const platformsCatalog = platformsCatalogQuery.data;
  const channelsCatalog = channelsCatalogQuery.data;

  const isPending = [
    channelsGlossaryListQuery,
    platformsGlossaryListQuery,
    channelsCatalogQuery,
    platformsCatalogQuery,
  ].some((result) => result.isPending);

  return {
    isPending,
    channelsGlossaryList,
    platformsGlossaryList,
    platformsCatalog,
    channelsCatalog,
    isAllChannelGlossariesAdded:
      channelsGlossaryList.totalItems ===
      Object.keys(channelsCatalog.channelsByName).length,
    isAllPlatformGlossariesAdded:
      platformsGlossaryList.totalItems ===
      platformsCatalog.platformsList.length,
  };
};

export const useGlossaryListV1 = () => {
  const searchParams = {
    ...initialSearchParams,
    itemsPerPage: INFINITYISH,
  };

  const channelsGlossaryListQuery = useTypedQuery({
    ...DataIngestion.Glossaries.getGlossaryListQueryOptions({
      params: {
        glossaryType: 'channel',
        ...searchParams,
      },
    }),
    placeholderData: getPaginatedDataPlaceholder({
      codesPerGlossary: 0,
    }) as GlossaryListQueryResult,
    staleTime: 30 * 1000,
  });

  const platformsGlossaryListQuery = useTypedQuery({
    ...DataIngestion.Glossaries.getGlossaryListQueryOptions({
      params: {
        glossaryType: 'platform',
        ...searchParams,
      },
    }),
    placeholderData: getPaginatedDataPlaceholder({
      codesPerGlossary: 0,
    }) as GlossaryListQueryResult,
    staleTime: 30 * 1000,
  });

  const platformsCatalogQuery = usePlatformsCatalog();
  const channelsCatalogQuery = useChannelsCatalog();

  const channelsGlossaryList = channelsGlossaryListQuery.data;
  const platformsGlossaryList = platformsGlossaryListQuery.data;
  const platformsCatalog = platformsCatalogQuery.data;
  const channelsCatalog = channelsCatalogQuery.data;

  const isPending = [
    channelsGlossaryListQuery,
    platformsGlossaryListQuery,
    channelsCatalogQuery,
    platformsCatalogQuery,
  ].some((result) => result.isPending);

  return {
    isPending,
    channelsGlossaryList,
    platformsGlossaryList,
    platformsCatalog,
    channelsCatalog,
    isAllChannelGlossariesAdded:
      channelsGlossaryList.totalItems ===
      Object.keys(channelsCatalog.channelsByName).length -
        DISABLED_CHANNELS.length,
    isAllPlatformGlossariesAdded:
      platformsGlossaryList.totalItems ===
      platformsCatalog.platformsList.length,
  };
};

export const useGlossaryList = () => {
  const isImportHubV2 = useImportHubV2();

  if (isImportHubV2) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useGlossaryListV2();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useGlossaryListV1();
};

export const useAddGlossaryItemV1 = () => {
  const queryClient = useQueryClient();

  const glossaryQueryFilter = {
    predicate: (query: { queryKey: unknown }) =>
      Array.isArray(query.queryKey) &&
      query.queryKey.includes(GLOSSARY_KEY_SEGMENT),
  };

  return useEnhancedMutation({
    mutationFn: async (
      payload: DataIngestion.Glossaries.AddGlossaryItemV1Payload,
    ) => {
      const response = await DataIngestion.Glossaries.addGlossaryItem({
        data: payload,
      });

      await queryClient.refetchQueries(glossaryQueryFilter);

      return response.data;
    },
    onMutate() {
      queryClient.cancelQueries(glossaryQueryFilter);
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t(
          'Failed to add a new item to the glossary. Please try again',
        ),
      }),
    },
  });
};

export const useAddGlossaryItemV2 = () => {
  const queryClient = useQueryClient();

  const glossaryQueryFilter = {
    predicate: (query: { queryKey: unknown }) =>
      Array.isArray(query.queryKey) &&
      query.queryKey.includes(GLOSSARY_KEY_SEGMENT),
  };

  return useEnhancedMutation({
    mutationFn: async (
      payload: DataIngestion.Glossaries.AddGlossaryItemV1Payload,
    ) => {
      const response = await DataIngestion.Glossaries.addGlossaryItemV2({
        data: {
          ...payload,
          glossaryType: mdmFieldCodec.encode(payload.glossaryType),
        },
      });

      await queryClient.refetchQueries(glossaryQueryFilter);

      return response.data;
    },
    onMutate() {
      queryClient.cancelQueries(glossaryQueryFilter);
    },
    meta: {
      getErrorsMap: (t) => ({
        default: t(
          'Failed to add a new item to the glossary. Please try again',
        ),
      }),
    },
  });
};

export const useAddGlossaryItem = () => {
  const isImportHubV2 = useImportHubV2();

  if (isImportHubV2) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useAddGlossaryItemV2();
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useAddGlossaryItemV1();
};

export const useBulkGlossaryAliasesUpdate = () => {
  const queryClient = useQueryClient();
  const isImportHubV2 = useImportHubV2();

  return useMutation({
    mutationFn: async ({
      glossaryId,
      payload,
    }: {
      glossaryId: string;
      payload: DataIngestion.Glossaries.BulkGlossaryAliasesUpdatePayload;
    }) => {
      const mutationFn = isImportHubV2
        ? DataIngestion.Glossaries.bulkGlossaryAliasesUpdateV2
        : DataIngestion.Glossaries.bulkGlossaryAliasesUpdate;

      const response = await mutationFn({
        urlParams: { glossaryId },
        data: payload,
      });

      await queryClient.refetchQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes(GLOSSARY_KEY_SEGMENT),
      });

      return response.data.items;
    },
  });
};
